import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { colorConstants } from 'config/colorConstants';
import { Spin, Switch, Tabs } from 'antd';
import { TPODataContext } from 'contexts/TPOContext';

import { CustomModal } from '../../PricingSimulator/Modals/CreateEditProjectContainer';
import dayjs, { Dayjs } from 'dayjs';

//updating antd config from moment to dayjs
//antd uses moment js by default for reading value
import dayjsGenerateConfig from 'rc-picker/lib/generate/dayjs';
import generatePicker from 'antd/es/date-picker/generatePicker';
import { antIcon } from 'components/pages/Login';
import EventDetails from '../AddEventModal/EventDetails';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import { tradePromoOptimizationUrls } from 'config/urls';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { formatDatePreservingTimeValues, getConfig, sendNotification } from 'utils/helperMethods';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { ErrorMessages, tpoNotificationMessages } from 'utils/Enums';
import { queryClient } from 'services/reactQueryService';
import { BaseOptionType } from 'antd/lib/select';
import { CustomSelect } from 'components/molecules/PricingSimulator/DimSection';
import SearchBox from 'components/molecules/SearchBox';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { CustomTable } from 'components/molecules/PricingSimulator/ScenarioTableWrapper';
import { getImportedEventsTableColumns } from 'utils/getTradePromoOptimizationTableColumns';
import { useFetchPlansForImport } from 'hooks/tradePromoOptimization/events/useFetchPlansForImport';
import { useFetchEventsByPlanIdImport } from 'hooks/tradePromoOptimization/events/useFetchEventsByPlanIdImport';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import { UserContext } from 'contexts/ConfigContext';
import _ from 'lodash';
import { PopUpErrorObject } from 'types/tradePromoOptimization';
import ErrorPopover from '../ErrorPopOver';
//updated component that uses dayjs instead of moment
const DatePicker = generatePicker<Dayjs>(dayjsGenerateConfig);

type PlanVariable = {
    is_tpm: boolean;
    offset: number;
    limit: number;
    plan_id?: number;
    search_string: string;
    requesting_type: 'self' | 'parent' | 'child';
    customer_name?: string | null;
};

type Props = {
    open: boolean;
    onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    onSubmitFinish: Function;
};

const plansListData = [
    { label: 'plan 1', value: 'plan_1' },
    { label: 'plan 2', value: 'plan_2' },
];

const importTypeOptions = [
    {
        label: 'Self',
        value: 'self',
    },
    { label: 'Parent', value: 'parent' },
    { label: 'Child', value: 'child' },
];

const ImportEventFromPlanModal: FC<Props> = ({ open, onCancel, onSubmitFinish }) => {
    const { selectedPlanInfo, isTpmTab, promoConfigDictionary } = useContext(TPODataContext);
    const { appConfig } = useContext(UserContext);
    const tpoConfig = getConfig('tradePromoOptimization', appConfig);
    const limit = tpoConfig?.importPlansLimit;
    //default object -plan
    const defaultPlanVariableObject: PlanVariable = {
        is_tpm: true,
        offset: 0,
        limit: limit,
        plan_id: selectedPlanInfo?.planId,
        search_string: '',
        requesting_type: 'self',
        customer_name: null,
    };

    //default object  - event
    const defaultEventVariableObject = {
        is_tpm: true,
        offset: 0,
        limit: limit,
        search_string: '',
    };

    const [isTpm, setIsTpm] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState<BaseOptionType | null>(null);
    const [searchKey, setSearchKey] = useState<string>('');
    const [checkboxSelected, setCheckboxSelected] = useState<any>({});
    const [planSearchValue, setPlanSearchValue] = useState('');
    const [plansVariable, setPlansVariable] = useState<PlanVariable>({ ...defaultPlanVariableObject });
    const [eventsDataVariable, setEventsDataVariable] = useState<any>({ ...defaultEventVariableObject });
    const [importEventErrors, setImportEventErrors] = useState<PopUpErrorObject[]>([]);
    const [filteredData, setFilteredData] = useState<any>([]); // initialize with all events
    // const [childCustomer, setChildCustomer] = useState(null);
    const modalTitle = `Import Events from plan`;
    const modalButtonTitle = `Import`;
    const isSelfRequest = plansVariable?.requesting_type === 'self';

    function resetFields() {
        setIsTpm(true);
        setSelectedPlan(null);
        setCheckboxSelected({});
        setSearchKey('');
        setPlansVariable({ ...defaultPlanVariableObject });
        setEventsDataVariable({ ...defaultEventVariableObject });
        setEventsData(null);
        setImportEventErrors([]);
    }

    const eventSearchHandler = (newKey: string) => {
        setSearchKey(newKey);
        // unchecking select All if selected
        if (checkboxSelected?.selectAll) delete checkboxSelected.selectAll;
        setCheckboxSelected({ ...checkboxSelected });
    };
    //plans data api
    const {
        data: plansData,
        loading: plansDataLoading,
        error: plansDataError,
        count: plansDataCount,
    } = useFetchPlansForImport({
        method: 'get',
        queryConfig: {
            enabled:
                open &&
                Boolean(plansVariable?.plan_id) &&
                (isSelfRequest || (!isSelfRequest && Boolean(plansVariable?.customer_name))),
        },
        url: tradePromoOptimizationUrls.getPlansForImportUrl,
        queryKey: ['tpo-import-plan', 'tpo-import-events-plan-data', plansVariable],
        variables: { ...(plansVariable || {}) },
    });
    const { mutate: importEvent, loading: importEventLoading } = useCommonMutationHandler({
        url: tradePromoOptimizationUrls.importEventsFromPlanUrl,
        method: 'post',
    });

    //events data api
    const {
        data: eventsData,
        loading: eventsDataLoading,
        error: eventsDataError,
        count: eventsDataCount,
        setData: setEventsData,
    } = useFetchEventsByPlanIdImport({
        method: 'post',
        queryConfig: { enabled: open && 'plan_id' in eventsDataVariable },
        url: tradePromoOptimizationUrls.getEventsByPlanIdForImportUrl,
        queryKey: ['tpo-import-plan', 'tpo-import-events-data', eventsDataVariable],
        variables: { ...(eventsDataVariable || {}) },
        extras: { promoConfigDictionary },
    });

    const { data: childCustomers, loading: childCustomersLoading } = useCommonApiHandler({
        url: tradePromoOptimizationUrls.getChildCustomersForImport,
        method: 'get',
        queryKey: ['tpo-import-child-customers', selectedPlanInfo?.planId],
        queryConfig: { enabled: open && Boolean(selectedPlanInfo?.dim2Filter?.value?.[0]) },
        paramsVariables: { parent_customer_name: selectedPlanInfo?.dim2Filter?.value?.[0] },
        parserFunction: (data) => {
            return data.map((item: string) => ({
                label: item,
                value: item,
            }));
        },
    });

    useEffect(() => {
        //cleanup function
        return () => {
            resetFields();
            queryClient.removeQueries({
                queryKey: ['tpo-import-plan'],
                exact: false,
            });
        };
    }, [open, selectedPlanInfo]);

    useEffect(() => {
        setFilteredData(eventsData);
    }, [eventsData]);

    const onSubmit = () => {
        if (importEventLoading || importButtonDisabledCondition) return;
        //validate form and trigger api call
        const allEventSelected = Boolean(checkboxSelected?.['selectAll']);
        const tempCheckboxSelected = { ...checkboxSelected };
        delete tempCheckboxSelected?.['selectAll'];

        const eventPromoPair = Object.keys(tempCheckboxSelected).map((item) => {
            return {
                event_id: item,
                promo_ids: tempCheckboxSelected[item],
            };
        });

        const payload = {
            all_events_selected: allEventSelected,
            // event_ids: allEventSelected ? [] : Object.keys(checkboxSelected).map((key) => Number(key)),
            event_promo_pairs: eventPromoPair,
            target_plan_id: selectedPlanInfo?.planId,
            user_id: localStorage.getItem('email'),
            // source_plan_id: selectedPlan?.value,
            is_tpm_plan: selectedPlanInfo?.isTpmPlan,
            is_tpm_tab: isTpmTab,
        };
        // form.submit();
        importEvent(payload, {
            onSuccess: (data: any) => {
                if (data?.data?.warnings?.length > 0) {
                    const warnings: string[] = [];
                    data?.data?.warnings?.forEach((item: any) => {
                        warnings.push(item.description?.join(', '));
                    });
                    sendNotification('info', warnings.join(', '));
                } else {
                    sendNotification('success', tpoNotificationMessages.IMPORT_EVENT_SUCCESS);
                }
                onSubmitFinish();
                setImportEventErrors([]);
            },
            onError: (val: any) => {
                setImportEventErrors(val?.response?.data?.detail ?? []);

                if (val?.response?.data?.detail?.length > 0) {
                    sendNotification(
                        'error',
                        <span>
                            We encountered some errors. Please click on
                            <span className="text-redAlt mx-1">Error(s)</span>
                            link to view the error details.
                        </span>,
                    );
                } else {
                    sendNotification('error', ErrorMessages.GENERALIZED_ERROR_MSG, 10);
                } // onCancel(val);
            },
        });
        // form.submit();
    };

    //called when clicked on search icon or enter
    const onSearch = (val: string) => {
        setEventsDataVariable({ ...eventsDataVariable, offset: 0, search_string: val });
    };

    //debounced search handler
    const debouncedHandleSearch = useCallback(_.debounce(handleSearch, 400), [plansVariable]);

    function handleSearch(val: string) {
        // unchecking select All if selected
        if (checkboxSelected?.selectAll) delete checkboxSelected.selectAll;
        setCheckboxSelected({ ...checkboxSelected });
        setPlansVariable({ ...plansVariable, offset: 0, search_string: val });
    }

    //events list inf scroller function
    function infiniteScrollOnNextEvents(loading: boolean) {
        if (loading) return;
        const newOffset = eventsDataVariable?.offset + eventsDataVariable?.limit;
        // unchecking select All if selected
        if (checkboxSelected?.selectAll) delete checkboxSelected.selectAll;
        setCheckboxSelected({ ...checkboxSelected });
        setEventsDataVariable({ ...eventsDataVariable, offset: newOffset });
    }

    const memoisedColumns = useMemo(() => {
        const columns =
            eventsData?.length > 0
                ? [
                      { label: 'Event', key: 'event', sortKey: 'event' },
                      { label: 'Promo', key: 'promo', sortKey: 'promo' },
                      { label: 'Promo Type', key: 'promoType', sortKey: 'promoType', filterKey: 'promoType' },
                      { label: 'Tactic Type', key: 'tacticType', sortKey: 'tacticType' },
                      { label: 'Promo Start Date', key: 'promoStartDate', sortKey: 'promoStartDateUnix' },
                      { label: 'Promo End Date', key: 'promoEndDate', sortKey: 'promoEndDateUnix' },
                  ]
                : [];

        return getImportedEventsTableColumns(
            columns,
            filteredData,
            checkboxSelected,
            setCheckboxSelected,
            filteredData?.length,
        );
    }, [filteredData, checkboxSelected]);

    const importButtonDisabledCondition = Object.keys(checkboxSelected).length === 0;

    //list inf scroller function plans list
    const onInfiniteScrollOnNextMemoisedDropdown = useCallback(() => {
        if (plansDataLoading) return;
        const newOffset = plansVariable?.offset + plansVariable?.limit;
        setPlansVariable({ ...plansVariable, offset: newOffset });
    }, [plansVariable, plansDataLoading]);
    return (
        <div>
            <CustomModal
                style={{ borderRadius: 8 }}
                cancelButtonProps={{ disabled: importEventLoading, style: { borderRadius: 6 } }}
                okButtonProps={{ disabled: importButtonDisabledCondition, style: { borderRadius: 6 } }}
                okText={importEventLoading ? <Spin indicator={antIcon} style={{ color: 'white' }} /> : modalButtonTitle}
                maskClosable={false}
                destroyOnClose
                centered
                onOk={onSubmit}
                open={open}
                width={1000}
                onCancel={(e) => {
                    if (importEventLoading) return;
                    onCancel(e);
                }}
                title={
                    <div className="flex items-center">
                        {modalTitle}
                        <div className="flex mt-1 items-center">
                            <span className="font-xs text-promoColour ml-3 mr-1">TPM</span>
                            <Switch
                                checked={!isTpm}
                                onChange={(checked) => {
                                    setIsTpm(!checked);
                                    setPlansVariable({ ...plansVariable, offset: 0, is_tpm: !checked });
                                    setSelectedPlan(null);
                                    setEventsData(null);
                                    setImportEventErrors([]);
                                }}
                            />
                            <span className="font-xs text-promoColour ml-1">TPO</span>
                        </div>
                    </div>
                }
            >
                <div className="mb-2 grid grid-cols-2 gap-4 justify-between">
                    <div className="flex items-center gap-2">
                        <span className="w-28">Import Type: </span>
                        <SelectWithInfiniteScroller
                            hasMore={plansData?.length < plansDataCount}
                            placeholder={'Select Import Type'}
                            className="dropdown-height w-[250px]"
                            options={importTypeOptions}
                            value={plansVariable?.requesting_type}
                            onChange={(val, item) => {
                                const tempPlanVariable = {
                                    ...plansVariable,
                                    offset: 0,
                                    requesting_type: val,
                                    customer_name: val === 'parent' ? selectedPlanInfo?.dim2Filter?.value?.[0] : null,
                                };
                                setPlansVariable(tempPlanVariable);
                                setSelectedPlan(null);
                                setEventsData(null);
                                setImportEventErrors([]);
                                setEventsDataVariable({ ...defaultEventVariableObject, offset: 0 });
                                setSearchKey('');
                            }}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-28">Child Customer: </span>
                        <SelectWithInfiniteScroller
                            placeholder={'Select Child Customer'}
                            className="dropdown-height w-[250px]"
                            loading={childCustomersLoading}
                            options={childCustomers}
                            value={plansVariable?.requesting_type === 'child' ? plansVariable?.customer_name : null}
                            disabled={plansVariable?.requesting_type !== 'child'}
                            notFoundContent={
                                childCustomersLoading ? (
                                    <div className="p-3">
                                        <Loader />
                                    </div>
                                ) : (
                                    <ErrorBox msg="No Data" />
                                )
                            }
                            onChange={(val, item) => {
                                setSelectedPlan(null);
                                setEventsData(null);
                                setPlansVariable({ ...plansVariable, offset: 0, customer_name: val });
                                setImportEventErrors([]);
                                setEventsDataVariable({ ...defaultEventVariableObject, offset: 0 });
                                setSearchKey('');
                            }}
                        />
                        {importEventErrors?.length > 0 && <ErrorPopover promoPopupErrors={importEventErrors} />}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-28">Plan: </span>
                        <SelectWithInfiniteScroller
                            dropdownRender={(menu) => <div className="break-words whitespace-normal">{menu}</div>}
                            hasMore={plansData?.length < plansDataCount}
                            onInfiniteScrollOnNextMemoised={onInfiniteScrollOnNextMemoisedDropdown}
                            placeholder={'Select Plan'}
                            className="dropdown-height w-[250px]"
                            loading={plansDataLoading}
                            options={plansData}
                            value={selectedPlan?.value}
                            showSearch
                            disabled={plansVariable?.requesting_type === 'child' && !plansVariable?.customer_name}
                            filterOption={false}
                            searchValue={planSearchValue}
                            onSearch={(val) => {
                                setPlanSearchValue(val);
                                debouncedHandleSearch(val);
                            }}
                            notFoundContent={
                                plansDataLoading ? (
                                    <div className="p-3">
                                        <Loader />
                                    </div>
                                ) : (
                                    <ErrorBox msg="No Data" />
                                )
                            }
                            onChange={(val, item) => {
                                setSelectedPlan(item as BaseOptionType);
                                setEventsDataVariable({ ...eventsDataVariable, offset: 0, plan_id: val });
                                setImportEventErrors([]);
                            }}
                        />
                        {importEventErrors?.length > 0 && <ErrorPopover promoPopupErrors={importEventErrors} />}
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-28" />
                        <SearchBox onSearch={onSearch} search={searchKey} setSearch={eventSearchHandler} width={250} />
                    </div>
                </div>
                <InfiniteScroller
                    data={eventsData}
                    loading={eventsDataLoading}
                    hasMore={eventsData?.length < eventsDataCount || eventsDataLoading}
                    height={eventsData && !eventsDataError ? 500 : 'auto'}
                    onNext={() => infiniteScrollOnNextEvents(eventsDataLoading)}
                >
                    <CustomTable
                        onChange={(pagination, filter, sort: any, extra) => {
                            setCheckboxSelected({});
                            if (extra?.currentDataSource) {
                                setFilteredData(extra.currentDataSource);
                            } else {
                                setFilteredData(eventsData); // fallback
                            }

                            if (sort?.order) {
                                const sortMapping = {
                                    descend: 'desc',
                                    ascend: 'asc',
                                };

                                // Optional: your sort variable handling
                                // setTableReactQueryVariables({
                                //     ...tableReactQueryVariables,
                                //     sort_by: sorter?.column?.sortKey,
                                //     sorting_order: sortMapping?.[sorter?.order] || 'desc',
                                //     offset: 0,
                                // });
                            } else {
                                // Optional: reset sort
                            }
                        }}
                        sticky={{
                            getContainer: () =>
                                document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                        }} //adding x axis scrollbar on table
                        //loading={eventsDataLoading}
                        columns={memoisedColumns}
                        scroll={{ x: '100%' }}
                        dataSource={eventsData}
                        pagination={false}
                        style={{
                            opacity: eventsDataLoading ? '0.3' : 1,
                            pointerEvents: eventsDataLoading ? 'none' : 'auto',
                        }}
                        // locale={{
                        //     emptyText: eventsDataError ? <ErrorBox msg={'No Data'} /> : '',
                        // }}
                    />
                </InfiniteScroller>
            </CustomModal>
        </div>
    );
};

export default ImportEventFromPlanModal;
